import React, { type ButtonHTMLAttributes, useState } from 'react';

import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';

import { useBreakpoints } from '@pkg/hooks';

import { ReactComponent as DotsSvg } from '../../svg/dots.svg';

import { MenuBackdrop, MenuButton, MenuInside, MenuWrapper, TriggerButton } from './OptionsMenu.styles';

interface OptionMenuProps {
    children: React.ReactNode;
}

type OptionsMenuButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

export const OptionsMenuButton = (props: OptionsMenuButtonProps) => {
    return <MenuButton {...props} />;
};

export const OptionsMenu = ({ children }: OptionMenuProps) => {
    const [open, setOpen] = useState(false);
    const { isMobile } = useBreakpoints();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-end',
        middleware: [offset(5), flip({ fallbackAxisSideDirection: 'end' }), shift()],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const menuProps = !isMobile
        ? {
              style: floatingStyles,
              onClick: () => setOpen(false),
              ...getFloatingProps(),
          }
        : {
              onClick: () => setOpen(false),
          };

    const menuAnimationProps = !isMobile
        ? {
              initial: { opacity: 0, y: -10 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: -10 },
          }
        : {
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: 20 },
          };
    return (
        <>
            <TriggerButton ref={refs.setReference} {...getReferenceProps()} $isOpen={open}>
                <DotsSvg />
            </TriggerButton>
            {open && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false}>
                        <MenuWrapper ref={refs.setFloating} {...menuProps}>
                            <MenuInside {...menuAnimationProps}>{children}</MenuInside>
                            <MenuBackdrop />
                        </MenuWrapper>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
};
